import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../Navbar';
import Navigation from './Navigation';
import MobileNav from './MobileNav';
import ChatBot from '../ChatBot';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [hasAnnouncementBanner, setHasAnnouncementBanner] = useState(false);
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1023px)');
  const isDocumentationPage = location.pathname === '/documentation';

  useEffect(() => {
    setIsMobileNavOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isDropdownClick = target.closest('[data-dropdown]');
      const isNavClick = target.closest('[data-nav]');

      if (!isDropdownClick) {
        setIsDropdownOpen(false);
      }
      if (!isNavClick && isMobile) {
        setIsMobileNavOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  // Récupérer l'état du sidebar depuis localStorage au chargement
  useEffect(() => {
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState !== null) {
      setIsSidebarCollapsed(savedState === 'true');
    }
  }, []);

  // Écouter les changements de visibilité du bandeau d'annonce
  useEffect(() => {
    // Vérifier l'état initial
    setHasAnnouncementBanner(document.body.classList.contains('has-announcement-banner'));

    // Écouter les changements
    const handleBannerChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setHasAnnouncementBanner(customEvent.detail.isVisible);
    };

    document.addEventListener('announcementBannerVisibilityChange', handleBannerChange);
    
    return () => {
      document.removeEventListener('announcementBannerVisibilityChange', handleBannerChange);
    };
  }, []);

  // Sauvegarder l'état du sidebar dans localStorage
  const toggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', String(newState));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-900">
      {!isMobile && (
        <div 
          className={`hidden lg:block fixed inset-y-0 left-0 z-40`} 
          data-nav
        >
          <Navigation 
            isCollapsed={isSidebarCollapsed} 
            onToggleCollapse={toggleSidebar} 
          />
        </div>
      )}

      {isMobile && (
        <MobileNav 
          isOpen={isMobileNavOpen} 
          onToggle={() => setIsMobileNavOpen(!isMobileNavOpen)} 
        />
      )}

      <div 
        className={`${!isMobile ? (isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64') : ''} relative z-0 flex flex-col min-h-screen transition-all duration-300`}
      >
        <div className="sticky top-0 z-10">
          <Navbar 
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            onMobileMenuClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
            isSidebarCollapsed={isSidebarCollapsed}
            onToggleSidebar={toggleSidebar}
          />
        </div>
        <motion.main 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={`container mx-auto px-4 sm:px-6 lg:px-8 flex-grow ${
            isDocumentationPage ? 'py-0' : 'py-8'
          }`}
        >
          {children}
        </motion.main>
      </div>

      {!isMobile && <ChatBot />}
    </div>
  );
};

export default MainLayout;